import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { ProspectCatalogs, ProspectDetails, ProspectEvent, WaChannel, WaState } from "../../../services/v2/prospects.service";
import { cuando, errorTexto, etapaClase, KIND_FILTROS, localMxAIso, nombreCompleto, statusClase } from "./salesHelpers";
import Conversacion from "./conversacion";
import Compositor from "./compositor";
import LadoCliente from "./ladoCliente";
import EditarDatos from "./editarDatos";
import Llamada from "./llamada";
import PromoModal from "./promoModal";
import { aMarcable, FinLlamada, telefono } from "./telefono/telefono";
import { useTelefono } from "./telefono/telefonoUI";

interface Props {
	campusId: number;
	prospectId: number;
	catalogs: ProspectCatalogs | null;
	onChanged: () => void;
	onOpen: (id: number) => void;
	onBack: () => void;
	/** Marcador: la ficha llega con la llamada ya abierta y al guardar pasa al siguiente. */
	marcador?: { onResultado: (resultado: string) => void; autoLlamar: boolean; campaignContactId?: number | null } | null;
	/** Llevar la bandeja a una vista ("Ver lista" de la promo: Matriculado · Califican a promo). */
	onVista?: (etapa: number, view: string) => void;
}

const PAGE = 60;

/**
 * La ficha del cliente como en el lienzo (19-sep): arriba quién es y qué hacer (Llamar, Agendar, Inscribir),
 * al centro la conversación de WhatsApp con notas y llamadas mezcladas, abajo para escribir, a la derecha el contexto.
 */
const ClienteView: React.FC<Props> = ({ campusId, prospectId, catalogs, onChanged, onOpen, onBack, marcador, onVista }) => {
	const http = HttpManager.getInstance();
	const navigate = useNavigate();
	const [det, setDet] = useState<ProspectDetails | null>(null);
	const [events, setEvents] = useState<ProspectEvent[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [cargando, setCargando] = useState<boolean>(false);
	const [filtro, setFiltro] = useState<string>("todo");
	const [busca, setBusca] = useState<string>("");
	const [buscaAplicada, setBuscaAplicada] = useState<string>("");
	const [wa, setWa] = useState<{ state: WaState; channels: WaChannel[] } | null>(null);
	const [canal, setCanal] = useState<number | null>(null);
	const [texto, setTexto] = useState<string>("");
	const [panel, setPanel] = useState<"status" | "agendar" | "reasignar" | "vincular" | null>(null);
	const [modal, setModal] = useState<"editar" | "llamar" | "promo" | null>(null);
	const [mas, setMas] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// formularios de acciones
	const [statusId, setStatusId] = useState<number>(0);
	const [motivo, setMotivo] = useState<string>("");
	const [segFecha, setSegFecha] = useState<string>("");
	const [segMotivo, setSegMotivo] = useState<string>("");
	const [reasignarA, setReasignarA] = useState<string>("");
	const [vincTel, setVincTel] = useState<string>("");
	const [vincTipo, setVincTipo] = useState<string>("padre_madre");
	const [vincMatch, setVincMatch] = useState<any>(null);
	const vivo = useRef<number>(prospectId);
	const autoPendiente = useRef<boolean>(false);
	const tel = useTelefono();
	const [fin, setFin] = useState<FinLlamada | null>(null);
	// Al colgar una llamada de esta persona (saliente o entrante ligada) se abre "cómo terminó" con la duración ya puesta.
	useEffect(() => telefono.alTerminar((f) => { if (f.prospectId === prospectId) { setFin(f); setModal("llamar"); } }), [prospectId]);

	const kinds = useMemo(() => KIND_FILTROS.find((f) => f.key === filtro)?.kinds || [], [filtro]);

	const cargarDetalle = useCallback(async () => {
		try {
			const r = await http.prospectsService.details(campusId, prospectId);
			if (vivo.current !== prospectId) return;
			setDet(r.data);
			setStatusId(r.data.prospect.status_id);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, prospectId]);

	const cargarWa = useCallback(async (ch?: number | null) => {
		try {
			const r = await http.prospectsService.whatsapp(campusId, prospectId, ch ?? canal);
			if (vivo.current === prospectId) setWa(r.data);
		} catch (e) { setWa(null); }
	}, [campusId, prospectId, canal]);

	// La API da lo más nuevo primero; en pantalla va al revés (lo nuevo abajo).
	const cargarEventos = useCallback(async (p: number, modo: "reset" | "anteriores" | "nuevos") => {
		if (modo !== "nuevos") setCargando(true);
		try {
			const r = await http.prospectsService.events(campusId, prospectId, { kind: kinds, q: buscaAplicada }, PAGE, p);
			if (vivo.current !== prospectId) return;
			const lote = [...r.data.data].reverse();
			setTotal(r.data.total);
			if (modo === "reset") { setEvents(lote); setPage(1); }
			else if (modo === "anteriores") { setEvents((prev) => [...lote.filter((e) => !prev.some((x) => x.id === e.id)), ...prev]); setPage(p); }
			else {
				setEvents((prev) => {
					const nuevos = lote.filter((e) => !prev.some((x) => x.id === e.id));
					// estados de entrega (✓✓, leído) también cambian en mensajes ya pintados
					const act = prev.map((x) => { const y = lote.find((e) => e.id === x.id); return y && y.wa_status !== x.wa_status ? { ...x, wa_status: y.wa_status } : x; });
					return nuevos.length ? [...act, ...nuevos] : act;
				});
			}
		} catch (e) { if (modo !== "nuevos") setError(errorTexto(e)); }
		finally { if (modo !== "nuevos") setCargando(false); }
	}, [campusId, prospectId, kinds, buscaAplicada]);

	useEffect(() => {
		vivo.current = prospectId;
		setDet(null); setEvents([]); setPanel(null); setModal(null); setError(null); setCanal(null); setTexto(""); setMas(false);
		cargarDetalle();
		cargarWa(null);
		setFin(null);
		autoPendiente.current = !!marcador?.autoLlamar;
	}, [prospectId]);
	// Marcador automático: en cuanto carga la ficha, con el teléfono encendido marca solo; si no, abre el registro manual.
	useEffect(() => {
		if (!det || !autoPendiente.current || det.prospect.id !== prospectId) return;
		autoPendiente.current = false;
		if (!llamarAhora()) setModal("llamar");
	}, [det]);
	useEffect(() => { cargarEventos(1, "reset"); }, [prospectId, kinds, buscaAplicada]);

	// Lo que llegue mientras está abierta (WhatsApp entrante, acuses de lectura): cada 15 s.
	useEffect(() => {
		const t = setInterval(() => { if (document.visibilityState === "visible") { cargarEventos(1, "nuevos"); cargarWa(); } }, 15000);
		return () => clearInterval(t);
	}, [cargarEventos, cargarWa]);

	const accion = async (fn: () => Promise<any>) => {
		setBusy(true); setError(null);
		try {
			await fn();
			await Promise.all([cargarDetalle(), cargarEventos(1, "reset")]);
			setPanel(null);
			onChanged();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	/** Marca por el teléfono de WAPP si está encendido; devuelve false si hay que registrar a mano. */
	function llamarAhora(): boolean {
		const ph = det?.prospect?.phone_e164;
		if (!ph || !telefono.listo || telefono.ocupado) return false;
		return !!telefono.llamar(aMarcable(ph), { prospectId, name: det ? nombreCompleto(det.prospect) : null, campaignContactId: marcador?.campaignContactId || null });
	}
	const statusSel = catalogs?.statuses.find((s) => s.id === statusId);
	const guardarStatus = () => accion(() => http.prospectsService.status(campusId, prospectId, statusId, motivo || null));
	const guardarSeguimiento = (quitar = false) => accion(() => http.prospectsService.nextAction(campusId, prospectId, quitar ? null : localMxAIso(segFecha), quitar ? null : segMotivo));
	const guardarReasignar = () => reasignarA && accion(() => http.prospectsService.assign(campusId, prospectId, reasignarA, motivo || null));
	const buscarVinculo = async () => {
		setVincMatch(null);
		try {
			const r = await http.prospectsService.lookupPhone(campusId, vincTel);
			setVincMatch(r.data.match || { none: true, valid: r.data.valid });
		} catch (e) { setError(errorTexto(e)); }
	};
	const guardarVinculo = () => vincMatch?.id && accion(() => http.prospectsService.addLink(campusId, prospectId, vincMatch.id, vincTipo));
	const quitarVinculo = (linkId: number) => accion(() => http.prospectsService.removeLink(campusId, prospectId, linkId));

	/** Siguiente paso (lienzo): atajos que ponen status y seguimiento de una vez. */
	const paso = (p: "promesa" | "llamar" | "no_interesa") => {
		if (p === "no_interesa") {
			return accion(() => http.prospectsService.status(campusId, prospectId, 5, "no le interesa por ahora"));
		}
		setSegMotivo(p === "promesa" ? "Promesa de visita" : "Volver a llamar");
		if (p === "promesa") setStatusId(4);
		setPanel("agendar");
	};
	const guardarAgenda = async () => {
		if (segMotivo === "Promesa de visita" && det && det.prospect.status_id !== 4) {
			await accion(async () => {
				await http.prospectsService.status(campusId, prospectId, 4, null);
				await http.prospectsService.nextAction(campusId, prospectId, localMxAIso(segFecha), segMotivo);
			});
		} else {
			await guardarSeguimiento(false);
		}
	};

	if (!det) {
		return <div className="ventas-cliente"><div className="ventas-vacio">{error || "Cargando…"}</div></div>;
	}
	const p = det.prospect;
	const ubicacion = (() => {
		const c = catalogs?.campuses.find((x) => x.id === (p.campus_id || campusId));
		return c?.address ? `${c.name}\n${c.address}` : null;
	})();

	return (
		<div className="ventas-cliente cv">
			<div className="cli-cab">
				<button className="volver" onClick={onBack}>← Lista</button>
				<div className="quien">
					<div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
						<span className="nombre">{nombreCompleto(p)}</span>
						<span className="tel">{p.phone_e164 ? telefonoBonito(p.phone_e164) : "sin teléfono"}</span>
					</div>
					<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
						<span className={"chip " + etapaClase(p.etapa_id)}>{det.etapa}</span>
						<span className={"chip " + statusClase(p.status_id)}>{det.status}{p.potencial_motivo ? " · " + p.potencial_motivo : ""}</span>
						{p.next_action_at ? <span className="chip promesa">Seguimiento {cuando(p.next_action_at)}</span> : null}
						{det.wapp?.ofertas?.length ? <span className="chip objetivo">Objetivo: {Array.from(new Set(det.wapp.ofertas.map((o) => ({ promo: "Promo", kit: "Kit", mas_cursos: "Más cursos", retencion: "Retención", reingreso: "Reinscripción" } as any)[o.kind] || o.kind))).join(" · ")}</span> : null}
						{det.student ? <span className="chip">{det.student.identity_code}</span> : null}
						<span className="origen">{[det.source, p.ad_name ? "«" + p.ad_name + "»" : null, p.owner_zoho_name].filter(Boolean).join(" · ")}{p.first_contact_at ? " · desde " + cuando(p.first_contact_at) : ""}</span>
					</div>
				</div>
				<div className="cli-acc">
					<button className="btn-wa llamar" disabled={!p.phone_e164 || (tel.ocupado && tel.llamada?.prospectId !== prospectId)}
						onClick={() => { setFin(null); if (!llamarAhora()) setModal("llamar"); }}>{tel.ocupado && tel.llamada?.prospectId === prospectId ? "En llamada" : "Llamar"}</button>
					<button className="btn-wa" onClick={() => { setSegMotivo(""); setPanel(panel === "agendar" ? null : "agendar"); }}>Agendar</button>
					{p.etapa_id === 1 ? <button className="btn-wa pri" onClick={() => navigate("/enrollmentRequests", { state: { phone: p.phone_e164, name: nombreCompleto(p) } })}>Inscribir</button> : null}
					{p.etapa_id === 3 || p.etapa_id === 4 ? <button className="btn-wa pri" onClick={() => navigate("/enrollmentRequests", { state: { phone: p.phone_e164, name: nombreCompleto(p), reingreso: true } })}>Reinscribir</button> : null}
					{det.wapp?.active && det.wapp.promo?.best ? <button className="btn-wa" onClick={() => setModal("promo")}>Promo que aplica</button> : null}
					{det.student ? <button className="btn-wa" onClick={() => navigate(`/students?alumno=${det.student!.id}&plantel=${det.wapp?.active?.campus_id || campusId}`)}>Ficha de alumna</button> : null}
					<div className="cv-menu">
						<button className="btn-wa" aria-haspopup="menu" aria-expanded={mas} onClick={() => setMas(!mas)}>Más ▾</button>
						{mas ? (
							<div className="cv-menu-lista" role="menu" onClick={() => setMas(false)}>
								<button role="menuitem" onClick={() => setModal("editar")}>Editar datos</button>
								<button role="menuitem" onClick={() => setPanel("status")}>Cambiar status</button>
								<button role="menuitem" onClick={() => setPanel("vincular")}>Vincular con otra persona</button>
								{catalogs?.isSupervisor ? <button role="menuitem" onClick={() => setPanel("reasignar")}>Reasignar</button> : null}
							</div>
						) : null}
					</div>
				</div>
			</div>

			{error ? <div className="aviso error" style={{ margin: "10px 18px 0" }}>{error}</div> : null}

			{panel === "status" && catalogs ? (
				<div className="tarjeta cv-panel">
					<h4>Cambiar status</h4>
					<select value={statusId} onChange={(e) => { setStatusId(Number(e.target.value)); setMotivo(""); }} aria-label="Status">
						{catalogs.statuses.filter((s) => s.bool_manual || s.id === p.status_id).map((s) => <option key={s.id} value={s.id} disabled={!s.bool_manual}>{s.name}</option>)}
					</select>
					{statusSel?.bool_needs_reason ? (
						<select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo">
							<option value="">Motivo…</option>
							{catalogs.motivosPotencial.filter((m) => !["heredado de Zoho", "sin status en Zoho"].includes(m)).map((m) => <option key={m} value={m}>{m}</option>)}
						</select>
					) : null}
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || (!!statusSel?.bool_needs_reason && !motivo)} onClick={guardarStatus}>Guardar</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "agendar" ? (
				<div className="tarjeta cv-panel">
					<h4>{segMotivo === "Promesa de visita" ? "Promesa de visita" : "Agendar seguimiento"}</h4>
					<input type="datetime-local" value={segFecha} onChange={(e) => setSegFecha(e.target.value)} aria-label="Fecha y hora" />
					<input type="text" placeholder="Para qué (visita, 2ª llamada, manda comprobante…)" value={segMotivo} onChange={(e) => setSegMotivo(e.target.value)} aria-label="Para qué" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !segFecha} onClick={guardarAgenda}>Guardar</button>
						{p.next_action_at ? <button className="btn-wa" disabled={busy} onClick={() => guardarSeguimiento(true)}>Quitar el que tiene</button> : null}
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "reasignar" ? (
				<div className="tarjeta cv-panel">
					<h4>Reasignar</h4>
					<select value={reasignarA} onChange={(e) => setReasignarA(e.target.value)} aria-label="Asesora">
						<option value="">Elegir asesora…</option>
						{(catalogs?.advisors || []).map((a) => <option key={a.id} value={a.id}>{a.name}{a.roles[0] ? " — " + a.roles[0] : ""}</option>)}
					</select>
					<input type="text" placeholder="Motivo (queda en la bitácora)" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !reasignarA} onClick={guardarReasignar}>Reasignar</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "vincular" && catalogs ? (
				<div className="tarjeta cv-panel">
					<h4>Vincular con otra persona</h4>
					<div className="fila-botones">
						<input type="tel" placeholder="Teléfono de la otra persona" value={vincTel} onChange={(e) => setVincTel(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter" && vincTel.trim()) buscarVinculo(); }} aria-label="Teléfono de la otra persona" />
						<button className="btn-wa" onClick={buscarVinculo} disabled={!vincTel.trim()}>Buscar</button>
					</div>
					{vincMatch?.id ? (
						<>
							<div className="par"><span>Encontrado</span><span>{nombreCompleto(vincMatch)} · {vincMatch.etapa}</span></div>
							<select value={vincTipo} onChange={(e) => setVincTipo(e.target.value)} aria-label="Tipo de vínculo">
								{catalogs.linkKinds.map((k) => <option key={k} value={k}>{k.replace("_", " / ").replace("padre / madre", "padre o madre")}</option>)}
							</select>
							<div className="fila-botones"><button className="btn-wa pri" disabled={busy} onClick={guardarVinculo}>Vincular</button></div>
						</>
					) : vincMatch?.none ? <div className="aviso">{vincMatch.valid ? "Ese teléfono no está registrado." : "Ese teléfono no es válido."}</div> : null}
				</div>
			) : null}

			<div className="cv-cuerpo">
				<div className="cv-centro">
					<div className="cv-filtros">
						{KIND_FILTROS.map((f) => <button key={f.key} className={filtro === f.key ? "on" : ""} onClick={() => setFiltro(f.key)}>{f.label}</button>)}
						<input type="search" placeholder="Buscar en la conversación" value={busca} onChange={(e) => setBusca(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter") setBuscaAplicada(busca.trim()); }} aria-label="Buscar en la conversación" />
						<span className="cnt">{total.toLocaleString("es-MX")}</span>
					</div>
					<Conversacion campusId={campusId} prospectId={prospectId} events={events} hayMas={events.length < total}
						onMas={() => cargarEventos(page + 1, "anteriores")} cargando={cargando} />
					<Compositor campusId={campusId} prospectId={prospectId} firstName={(p.first_name || "").split(" ")[0]} countryId={p.country_id}
						wa={wa} texto={texto} setTexto={setTexto} ubicacion={ubicacion}
						onEnviado={() => { cargarEventos(1, "nuevos"); cargarWa(); cargarDetalle(); onChanged(); }}
						onCanal={(id) => { setCanal(id); cargarWa(id); }} />
				</div>
				<LadoCliente campusId={campusId} det={det} onEditar={() => setModal("editar")}
					onEnviarAlChat={(t) => setTexto((texto ? texto + "\n\n" : "") + t)} onPaso={paso} onAbrir={onOpen} onQuitarVinculo={quitarVinculo}
					onPromo={() => setModal("promo")} onAgendar={(motivo) => { setSegMotivo(motivo); setPanel("agendar"); }}
					catalogs={catalogs} onAccion={accion} />
			</div>

			{modal === "editar" ? <EditarDatos campusId={campusId} det={det} catalogs={catalogs} onClose={() => setModal(null)}
				onGuardado={(d) => { setDet(d); setModal(null); cargarEventos(1, "nuevos"); onChanged(); }} /> : null}
			{modal === "promo" ? <PromoModal campusId={campusId} det={det} asesora={p.owner_zoho_name || ""} onClose={() => setModal(null)}
				onAlChat={(t) => setTexto(t)} wa={wa} onEnviado={() => { cargarEventos(1, "nuevos"); cargarWa(); cargarDetalle(); onChanged(); }}
				onVerLista={onVista ? () => onVista(2, "califican_promo") : undefined} /> : null}
			{modal === "llamar" && p.phone_e164 ? <Llamada campusId={campusId} prospectId={prospectId} nombre={nombreCompleto(p)} telefono={p.phone_e164}
				origen={marcador ? "marcador" : "manual"} auto={fin} campaignContactId={marcador?.campaignContactId || null} onClose={() => { setModal(null); setFin(null); }}
				onGuardado={(r) => { setModal(null); cargarEventos(1, "nuevos"); cargarDetalle(); onChanged(); marcador?.onResultado(r); }} /> : null}
		</div>
	);
};

export default ClienteView;
